import { Api, BASE_URL, BOTS_BASE_URL } from '@/api/index';
import axios, { AxiosRequestConfig } from 'axios';
import {
  Bot,
  FacebookPage,
  SubscribeFacebookPagePayload,
  SubscribeFacebookPageResponse,
  SubscribeFacebookPageResult,
} from 'ignite360-core';

export interface FacebookFeedItem {
  id: string;
  title: string;
  content: string;
  mediaType: string;
  thumbnailUrl: string | null;
}

export class FacebookApi {
  static async loadFacebookPages(
    facebookUserId: string,
    accessToken: string,
  ): Promise<FacebookPage[]> {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BASE_URL}/public/facebook/pages`,
      data: {
        facebookUserId,
        accessToken,
      },
    };
    const result = await axios.request<FacebookPage[]>(config);
    if (result.status === 200 && result.data) {
      return result.data;
    }
    return [];
  }

  static async connectBotWithFacebookPage(
    botId: string,
    data: SubscribeFacebookPagePayload,
  ): Promise<SubscribeFacebookPageResponse> {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BASE_URL}/public/bots/${botId}/facebook/subscribe`,
      data,
    };
    const result = await axios.request<SubscribeFacebookPageResponse>(config);
    if (result.status === 200) {
      return result.data;
    }
    throw new Error('Could not connect bot to facebook page!');
  }

  static async disconnectBotFromFacebookPage(botId: string): Promise<void> {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BASE_URL}/public/bots/${botId}/facebook/unsubscribe`,
    };

    const result = await axios.request<SubscribeFacebookPageResult>(config);
    if (result.status === 204) {
      return;
    }
    throw new Error('Could not disconnect bot from facebook page!');
  }

  static async publishTestPost(
    id: string,
    params?: { pageId: string; pageAccessToken: string },
  ): Promise<void> {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: `${BASE_URL}/public/bots/${id}/facebook/publish-listing`,
      data: params,
    };

    const result = await axios.request(config);
    if (result.status === 204) {
      return;
    }
    throw new Error('Could not publish post.');
  }

  static async publishPostForListing(id: string, listingId: string): Promise<Bot> {
    const config = Api.finalizeConfig({
      method: 'POST',
      url: `${BOTS_BASE_URL}/${id}/facebook/publish-listing`,
      data: {
        listingId,
      },
    });

    const result = await axios.request<Bot>(config);
    if (result.status === 200 && result.data) {
      return result.data;
    }
    throw new Error('Could not publish post for listing.');
  }

  static async getRecentPosts(botId: string): Promise<FacebookFeedItem[]> {
    const config = Api.finalizeConfig({
      method: 'GET',
      url: `${BOTS_BASE_URL}/${botId}/facebook/recent-posts`,
    });
    const result = await axios.request<FacebookFeedItem[]>(config);
    if (result.status === 200 && result.data) {
      return result.data;
    }
    return [];
  }

  /**
   * Downloads a post's media file through the API (avoids CORS on Facebook CDNs) for cross-posting.
   */
  static async downloadMediaFile(
    botId: string,
    postId: string,
  ): Promise<{ file: File; kind: 'image' | 'video' }> {
    const config = Api.finalizeConfig({
      method: 'GET',
      url: `${BOTS_BASE_URL}/${botId}/facebook/posts/${encodeURIComponent(postId)}/media/file`,
      responseType: 'arraybuffer',
    });
    const result = await axios.request<ArrayBuffer>(config);
    if (result.status !== 200 || !result.data) {
      throw new Error('Could not download media.');
    }
    const contentType = (result.headers['content-type'] as string) || 'application/octet-stream';
    const exposeKind = result.headers['x-facebook-media-kind'] as string | undefined;
    const nameMatch = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/.exec(
      (result.headers['content-disposition'] as string) || '',
    );
    const rawName = nameMatch ? decodeURIComponent(nameMatch[1] || nameMatch[2]) : '';
    const treatAsVideo = exposeKind === 'video' || contentType.startsWith('video/');
    const kind: 'image' | 'video' = treatAsVideo ? 'video' : 'image';
    const fileName = rawName || `facebook-${postId}${kind === 'video' ? '.mp4' : '.jpg'}`;
    const blob = new Blob([result.data], { type: contentType });
    return {
      file: new File([blob], fileName, { type: contentType }),
      kind,
    };
  }

  /** Permanently deletes a Facebook page post on the connected account. */
  static async deletePost(botId: string, postId: string): Promise<void> {
    const config = Api.finalizeConfig({
      method: 'DELETE',
      url: `${BOTS_BASE_URL}/${botId}/facebook/posts/${encodeURIComponent(postId)}`,
    });
    const result = await axios.request(config);
    if (result.status === 204) {
      return;
    }
    throw new Error('Could not delete Facebook post.');
  }
}
